import { useEffect, useRef } from 'react';
import type { NavigateFunction } from 'react-router-dom';

/**
 * Convierte clics en `<a href="/ruta-interna">` en navegación SPA (React Router),
 * evitando recarga completa del documento. Mismo comportamiento que Shopify admin.
 *
 * Usar en cada layout del panel que **no** sea hijo de `AppLayout` (ej. SettingsLayout,
 * pantallas full-screen como el theme editor), y centralizado también en `AppLayout`.
 */
export function useSpaInternalNavCapture(navigate: NavigateFunction): void {
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey) return;
      const el = (e.target as Node) && 'closest' in (e.target as Element) ? (e.target as Element).closest('a[href]') : null;
      if (!(el instanceof HTMLAnchorElement)) return;
      const href = el.getAttribute('href') || '';
      if (href.startsWith('#') || href.startsWith('javascript:') || href.startsWith('mailto:') || href.startsWith('tel:'))
        return;
      if (href.startsWith('//') || (href.startsWith('http') && !href.startsWith(window.location.origin))) return;
      const path = href.startsWith('http') ? new URL(href).pathname : href;
      if (!path.startsWith('/')) return;
      e.preventDefault();
      const url = href.startsWith('http') ? new URL(href) : new URL(href, window.location.origin);
      navigateRef.current(url.pathname + url.search + url.hash);
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, []);
}
